import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../actions/auth';
import { useNavigate } from 'react-router-dom';

const INACTIVITY_LIMIT_MS = 15 * 60 * 1000; // 15 minutes in milliseconds
const THROTTLE_INTERVAL_MS = 3000; // Throttle storage writes to once every 3 seconds

export default function InactivityTracker() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth || {});
  const intervalRef = useRef(null);
  const lastWriteRef = useRef(0);

  useEffect(() => {
    // Only track if user is logged in
    if (!user) return;

    const performLogout = () => {
      try {
        localStorage.removeItem('user');
        localStorage.removeItem('lastActivityTime');
      } catch (e) {
        // ignore storage errors
      }
      dispatch(logout());
      navigate('/login', { replace: true });
    };

    // Check if inactivity has exceeded 15 minutes
    const checkInactivity = () => {
      try {
        const last = localStorage.getItem('lastActivityTime');
        if (last) {
          const diff = Date.now() - parseInt(last, 10);
          if (diff >= INACTIVITY_LIMIT_MS) {
            performLogout();
            return true;
          }
        }
      } catch (e) {
        // fallback
      }
      return false;
    };

    // 1. Initial Mount Check:
    // If user returned after mobile tab sleep / refresh and 15 mins already elapsed, logout immediately.
    // Do NOT overwrite lastActivityTime before checking!
    const isExpired = checkInactivity();
    if (isExpired) return;

    // If within valid timeframe, initialize lastActivityTime if missing
    if (!localStorage.getItem('lastActivityTime')) {
      localStorage.setItem('lastActivityTime', Date.now().toString());
    }

    // Function to update lastActivityTime during active physical user interactions
    const updateActivity = () => {
      const now = Date.now();
      if (now - lastWriteRef.current > THROTTLE_INTERVAL_MS) {
        lastWriteRef.current = now;
        try {
          localStorage.setItem('lastActivityTime', now.toString());
        } catch (e) {
          // ignore
        }
      }
    };

    // When user leaves the tab/phone (locks screen, switches apps, backgrounds tab)
    const handleLeave = () => {
      try {
        localStorage.setItem('lastActivityTime', Date.now().toString());
      } catch (e) {
        // ignore
      }
    };

    // When user returns to tab/phone (unlocks screen, switches back to browser tab)
    const handleResume = () => {
      const expired = checkInactivity();
      if (!expired) {
        updateActivity();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleLeave();
      } else if (document.visibilityState === 'visible') {
        handleResume();
      }
    };

    // Listen to tab sync (if logged out in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'user' && !e.newValue) {
        performLogout();
      }
    };

    // Periodic check every 5 seconds while page is running
    intervalRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') {
        checkInactivity();
      }
    }, 5000);

    // Lifecycle events for mobile app switching and tab changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handleLeave);
    window.addEventListener('pageshow', handleResume);
    window.addEventListener('blur', handleLeave);
    window.addEventListener('focus', handleResume);
    window.addEventListener('storage', handleStorageChange);

    // Physical interaction events on mobile and desktop
    const interactionEvents = [
      'touchstart',
      'touchend',
      'touchmove',
      'scroll',
      'click',
      'mousedown',
      'mousemove',
      'keydown',
    ];

    interactionEvents.forEach((evt) => {
      window.addEventListener(evt, updateActivity, { passive: true });
    });

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handleLeave);
      window.removeEventListener('pageshow', handleResume);
      window.removeEventListener('blur', handleLeave);
      window.removeEventListener('focus', handleResume);
      window.removeEventListener('storage', handleStorageChange);
      interactionEvents.forEach((evt) => {
        window.removeEventListener(evt, updateActivity);
      });
    };
  }, [user, dispatch, navigate]);

  return null;
}
